import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import offlineStorage from '../services/OfflineStorage'
import syncService from '../services/SyncService'
import { vendaService, clienteService, produtoService } from '../services/api'

export const useVendaStore = defineStore('venda', () => {
  // ============ STATE ============
  const carrinho = ref([])
  const clienteSelecionado = ref(null)
  const formasPagamento = ref([
    { value: 'dinheiro', label: 'Dinheiro', icon: 'fa-solid fa-money-bill-wave' },
    { value: 'pix', label: 'Pix', icon: 'fa-solid fa-mobile-screen-button' },
    { value: 'cartao', label: 'Cartão', icon: 'fa-solid fa-credit-card' },
    { value: 'fiado', label: 'Fiado', icon: 'fa-solid fa-file-invoice' },
    { value: 'conta', label: 'Conta', icon: 'fa-solid fa-building-columns' }
  ])
  const isOnline = ref(navigator.onLine)
  const isSyncing = ref(false)
  const lastSync = ref(null)
  const hasPendingSync = ref(false)

  // ============ GETTERS ============
  const totalCarrinho = computed(() => {
    return carrinho.value.reduce((total, item) => total + item.subtotal, 0)
  })

  const quantidadeItens = computed(() => {
    return carrinho.value.reduce((total, item) => total + item.quantidade, 0)
  })

  const podeVenderFiado = computed(() => {
    if (!clienteSelecionado.value) return false
    const cliente = clienteSelecionado.value
    return cliente.credito_disponivel >= totalCarrinho.value && 
           (cliente.status === 'ativo' || cliente.status === 'bloqueado')
  })

  const podePagarComConta = computed(() => {
    if (!clienteSelecionado.value) return false
    const cliente = clienteSelecionado.value
    const saldoTotalDisponivel = cliente.saldo_atual + cliente.credito_disponivel
    return saldoTotalDisponivel >= totalCarrinho.value && 
           cliente.status !== 'inadimplente'
  })

  const valorSaldoUsado = computed(() => {
    if (!clienteSelecionado.value) return 0
    const cliente = clienteSelecionado.value
    return Math.min(cliente.saldo_atual, totalCarrinho.value)
  })

  const valorFiadoUsado = computed(() => {
    if (!clienteSelecionado.value) return 0
    const cliente = clienteSelecionado.value
    const valorFaltante = totalCarrinho.value - cliente.saldo_atual
    return Math.max(0, valorFaltante)
  })

  const usaPagamentoMisto = computed(() => {
    return valorFiadoUsado.value > 0
  })

  // ============ ACTIONS ============
  
  // Adicionar item ao carrinho
  function adicionarItem(produto, quantidade = 1) {
    const itemExistente = carrinho.value.find(item => item.produto_id === produto.id)
    
    if (itemExistente) {
      itemExistente.quantidade += quantidade
      itemExistente.subtotal = itemExistente.quantidade * itemExistente.valor_unitario
    } else {
      carrinho.value.push({
        produto_id: produto.id,
        nome: produto.nome,
        valor_unitario: produto.preco_venda,
        quantidade: quantidade,
        subtotal: produto.preco_venda * quantidade,
        estoque_disponivel: produto.quantidade_estoque
      })
    }
  }

  // Remover item do carrinho
  function removerItem(produtoId) {
    const index = carrinho.value.findIndex(item => item.produto_id === produtoId)
    if (index > -1) {
      carrinho.value.splice(index, 1)
    }
  }

  // Atualizar quantidade
  function atualizarQuantidade(produtoId, quantidade) {
    const item = carrinho.value.find(item => item.produto_id === produtoId)
    if (item) {
      item.quantidade = quantidade
      item.subtotal = item.quantidade * item.valor_unitario
    }
  }

  // Limpar carrinho
  function limparCarrinho() {
    carrinho.value = []
    clienteSelecionado.value = null
  }

  // Selecionar cliente
  function selecionarCliente(cliente) {
    clienteSelecionado.value = cliente
  }

  // Finalizar venda
  async function finalizarVenda(formaPagamento, observacoes = '') {
    if (carrinho.value.length === 0) {
      throw new Error('Carrinho vazio')
    }

    const vendas = []

    // Criar uma venda para cada item (simplificação)
    for (const item of carrinho.value) {
      const vendaData = {
        produto_id: item.produto_id,
        quantidade: item.quantidade,
        valor_unitario: item.valor_unitario,
        forma_pagamento: formaPagamento,
        observacoes: observacoes
      }

      // Adicionar cliente se estiver selecionado (para verificação de restrições)
      if (clienteSelecionado.value) {
        vendaData.cliente_id = clienteSelecionado.value.id
      }

      // Se for fiado, verificar cliente e limite de crédito
      if (formaPagamento === 'fiado') {
        if (!clienteSelecionado.value) {
          throw new Error('Selecione um cliente para venda fiado')
        }
        
        // Verificar limite de crédito
        if (!podeVenderFiado.value) {
          throw new Error(`Limite de crédito insuficiente. Disponível: R$ ${clienteSelecionado.value.credito_disponivel.toFixed(2)}`)
        }
      }

      // Se for pagamento com conta, verificar cliente e saldo + crédito
      if (formaPagamento === 'conta') {
        if (!clienteSelecionado.value) {
          throw new Error('Selecione um cliente para pagamento com conta')
        }
        
        // Verificar saldo + crédito
        if (!podePagarComConta.value) {
          throw new Error(`Saldo e crédito insuficientes. Saldo: R$ ${clienteSelecionado.value.saldo_atual.toFixed(2)}, Crédito: R$ ${clienteSelecionado.value.credito_disponivel.toFixed(2)}`)
        }
      }

      vendas.push(vendaData)
    }

    // Tentar enviar para API se estiver online
    if (isOnline.value) {
      try {
        const resultados = []
        
        for (const vendaData of vendas) {
          const { data } = await vendaService.criar(vendaData)
          resultados.push(data)
        }

        limparCarrinho()
        return { success: true, vendas: resultados, offline: false }
        
      } catch (error) {
        // Se falhar por erro de rede, salvar offline
        if (error.code === 'ERR_NETWORK' || !navigator.onLine) {
          return await salvarVendaOffline(vendas)
        }
        throw error
      }
    } else {
      // Modo offline - salvar localmente
      return await salvarVendaOffline(vendas)
    }
  }

  // Salvar venda offline
  async function salvarVendaOffline(vendas) {
    const vendasOffline = []
    
    for (const vendaData of vendas) {
      const vendaOffline = await offlineStorage.salvarVendaPendente(vendaData)
      vendasOffline.push(vendaOffline)
    }

    limparCarrinho()
    await updatePendingSyncStatus()
    
    return {
      success: true,
      vendas: vendasOffline,
      offline: true,
      message: 'Venda salva offline. Será sincronizada quando houver conexão.'
    }
  }

  // Atualizar status de pendência de sync
  async function updatePendingSyncStatus() {
    hasPendingSync.value = await offlineStorage.contarVendasPendentes() > 0
  }

  // Sincronizar vendas pendentes
  async function syncPendingSales() {
    if (isSyncing.value) return
    
    isSyncing.value = true
    
    try {
      const result = await syncService.syncPendingData()
      lastSync.value = new Date()
      await updatePendingSyncStatus()
      return result
    } finally {
      isSyncing.value = false
    }
  }

  // Atualizar status de conexão
  function updateOnlineStatus() {
    isOnline.value = navigator.onLine
  }

  // Buscar clientes
  async function buscarClientes(termo) {
    if (termo.length < 2) return []
    
    try {
      if (isOnline.value) {
        const { data } = await clienteService.buscarRapido(termo)
        // Atualizar cache
        await offlineStorage.salvarClientesCache(data.data)
        return data.data
      } else {
        // Usar cache
        return await offlineStorage.buscarClientesCachePorNome(termo)
      }
    } catch (error) {
      // Fallback para cache
      return await offlineStorage.buscarClientesCachePorNome(termo)
    }
  }

  // Buscar produtos
  async function buscarProdutos(termo) {
    if (termo.length < 2) return []
    
    try {
      if (isOnline.value) {
        const { data } = await produtoService.buscarRapido(termo)
        // Atualizar cache
        await offlineStorage.salvarProdutosCache(data.data)
        return data.data
      } else {
        // Usar cache
        return await offlineStorage.buscarProdutosCachePorNome(termo)
      }
    } catch (error) {
      // Fallback para cache
      return await offlineStorage.buscarProdutosCachePorNome(termo)
    }
  }

  // Setup listeners
  function setupListeners() {
    window.addEventListener('online', updateOnlineStatus)
    window.addEventListener('offline', updateOnlineStatus)
    
    // Tentar sincronizar quando voltar online
    window.addEventListener('online', () => {
      updatePendingSyncStatus()
      syncPendingSales()
    })
  }

  return {
    // State
    carrinho,
    clienteSelecionado,
    formasPagamento,
    isOnline,
    isSyncing,
    lastSync,
    hasPendingSync,
    
    // Getters
    totalCarrinho,
    quantidadeItens,
    podeVenderFiado,
    podePagarComConta,
    valorSaldoUsado,
    valorFiadoUsado,
    usaPagamentoMisto,
    
    // Actions
    adicionarItem,
    removerItem,
    atualizarQuantidade,
    limparCarrinho,
    selecionarCliente,
    finalizarVenda,
    salvarVendaOffline,
    syncPendingSales,
    updatePendingSyncStatus,
    updateOnlineStatus,
    buscarClientes,
    buscarProdutos,
    setupListeners
  }
})
